/**
 * Движок ИИ-заданий (D27): правила админки без React.
 *
 * Договор — `docs/CONTRACTS/ai-task-pipeline.md`. Правила перенесены из песочницы
 * `docs/refs/ai-pipeline-sandbox/index.html`, проверенной на стенде 11.09.2026, и
 * сверяются с договором и моделью службы в `aiPipeline.test.ts`.
 *
 * ⚠️ ПРОВЕРКИ ИИ НИЧЕГО НЕ ОДОБРЯЮТ. Здесь только то, что человек может нажать и
 * когда: решает проверяющая, вердикты ей в помощь.
 */

export const BUSY_STATES = ['queued', 'generating', 'checking_quality', 'checking_format'] as const;
export const STATE_ORDER = ['queued', 'generating', 'checking_quality', 'checking_format', 'done'] as const;
export const STAGES = ['generate', 'quality', 'format'] as const;
export type Stage = (typeof STAGES)[number];

/** Раз в сколько опрашивать элемент, пока идёт работа (договор: 3–5 с). */
export const POLL_MS = 4000;
export const PAGE_SIZE = 50;

export const SKILLS = ['reading', 'listening', 'writing', 'speaking'] as const;
export const LEVELS = ['A2', 'B1', 'B2', 'C1', 'C2'] as const;

/**
 * Виды для генерации — `ReadingTaskTypes` и `ListeningTaskTypes` службы без видов с
 * картинкой: `diagram_label` и карты генератор не делает, сервер ответил бы `image_required`.
 * Каталоги `api/ieltsMock.ts` сюда не годятся: там есть `diagram_label`, а аудирование урезано.
 */
export const GEN_READING_TYPES = [
  'mc_single', 'mc_multi', 'true_false_ng', 'yes_no_ng', 'matching_info', 'matching_headings',
  'matching_features', 'matching_sentence_endings', 'sentence_completion', 'summary_completion',
  'summary_completion_bank', 'note_completion', 'table_completion', 'flowchart_completion', 'short_answer',
] as const;
export const GEN_LISTENING_TYPES = [
  'mc_single', 'mc_multi', 'matching', 'form_completion', 'note_completion', 'table_completion',
  'flowchart_completion', 'summary_completion', 'sentence_completion', 'short_answer',
] as const;
/** Письмо: Academic Task 1 не генерируется — нужен график. */
export const GEN_WRITING_SUBTYPES = {
  task2: ['opinion', 'discussion', 'advantages_disadvantages', 'problem_solution', 'two_part'],
  task1_general: ['letter_formal', 'letter_semiformal', 'letter_informal'],
} as const;
export type WritingKind = keyof typeof GEN_WRITING_SUBTYPES;
export const MAX_GEN_TYPES = 6;

/**
 * Части IELTS Listening (договор §10): одна запись — одна часть на 5–7 минут, две группы по
 * порядку звучания — первый вид для вопросов 1–5 (первая половина), второй для 6–10 (вторая);
 * один вид — обе группы этого вида. Виды по частям — правило методолога 06.10.2026. Копия таблицы
 * `listeningParts` службы (`internal/service/listening_parts.go`); сверка — в `aiPipeline.test.ts`.
 * Служба отдаёт ту же таблицу ручкой `GET /admin/ielts/pipeline/listening-parts` — экран заказа
 * перейдёт на неё (F-админ), и копия уйдёт.
 */
export const LISTENING_PARTS = {
  1: ['form_completion', 'note_completion', 'table_completion'],
  2: ['mc_single', 'mc_multi', 'matching', 'note_completion', 'table_completion'],
  3: ['mc_single', 'mc_multi', 'matching', 'sentence_completion', 'summary_completion'],
  4: ['note_completion', 'table_completion', 'summary_completion', 'sentence_completion'],
} as const;
export type ListeningPart = keyof typeof LISTENING_PARTS;
export const LISTENING_PART_NUMS = [1, 2, 3, 4] as const;
export const MAX_LISTENING_TYPES = 2;

/** Сколько видов можно отметить: аудированию — 1–2 (одна часть), чтению — до шести. */
export function maxGenTypes(skill: string): number {
  return skill === 'listening' ? MAX_LISTENING_TYPES : MAX_GEN_TYPES;
}

/** Виды к выбору: чтение — каталог, аудирование — строка выбранной части. */
export function genCatalog(skill: string, part: ListeningPart): readonly string[] {
  if (skill === 'reading') return GEN_READING_TYPES;
  if (skill === 'listening') return LISTENING_PARTS[part];
  return [];
}

/**
 * Машинные коды отказов — таблица §0 договора. Причину различаем только по `code`:
 * `message` для человека и может меняться.
 */
export const PIPELINE_CODES = [
  'invalid_request', 'image_required', 'comment_required', 'exclude_foreign', 'rating_invalid',
  'prompt_unknown_key', 'prompt_placeholder_missing', 'prompt_placeholder_unknown', 'item_not_found',
  'prompt_not_found', 'pipeline_busy', 'pipeline_not_pending', 'pipeline_not_approved', 'stages_incomplete',
  'prompt_exists', 'prompt_stale', 'generation_failed',
  'listening_type_not_in_part', 'listening_types_limit', 'listening_group_excluded', 'listening_group_short',
] as const;
export const CODE_HINT_KEY: Record<string, string> = Object.fromEntries(
  PIPELINE_CODES.map((c) => [c, `adminAiPipeline.code.${c}`]),
);

type ItemLike =
  | { pipeline_state?: string; review_status?: string; checks_passed?: boolean | null; published_at?: string | null }
  | null
  | undefined;
type RunLike = { stage: string; attempt: number; verdict?: unknown };

export function isBusy(item: ItemLike): boolean {
  return !!item && (BUSY_STATES as readonly string[]).includes(item.pipeline_state ?? '');
}

/** `refetchInterval` для react-query: опрашиваем, только пока идёт работа. */
export function pollInterval(item: ItemLike): number | false {
  return isBusy(item) ? POLL_MS : false;
}

/** Попытки этапа по порядку номеров. */
export function runsOf<T extends RunLike>(runs: T[] | null | undefined, stage: string): T[] {
  return (runs ?? []).filter((r) => r.stage === stage).sort((a, b) => a.attempt - b.attempt);
}

/** Последняя попытка этапа — по номеру, а не по месту в массиве. */
export function lastRun<T extends RunLike>(runs: T[] | null | undefined, stage: string): T | null {
  const rs = runsOf(runs, stage);
  return rs.length ? rs[rs.length - 1] : null;
}

// ── Вердикты проверок ──

export type CheckIssue = { question_ord?: number | string; problem?: unknown; suggestion?: unknown };
export type TaskCheck = { pass?: boolean | null; issues: CheckIssue[] };
export type TaskVerdict = { quality?: TaskCheck; format?: TaskCheck };
export type PlacedIssue = CheckIssue & { stage: 'quality' | 'format' };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Вердикты последних попыток обеих проверок по заданиям. `task_index` — сквозной
 * номер с нуля по порядку tasks → writing → speaking: так содержимое уходит модели
 * (`checkPayload` в `service/ai_pipeline.go`).
 */
export function verdictsByTask(runs: RunLike[] | null | undefined): Record<number, TaskVerdict> {
  const out: Record<number, TaskVerdict> = {};
  for (const stage of ['quality', 'format'] as const) {
    const verdict = lastRun(runs, stage)?.verdict;
    const tasks = isObj(verdict) && Array.isArray(verdict.tasks) ? verdict.tasks : [];
    for (const t of tasks) {
      if (!isObj(t)) continue;
      const i = Number(t.task_index);
      if (!Number.isInteger(i) || i < 0) continue;
      (out[i] ||= {})[stage] = {
        pass: typeof t.pass === 'boolean' ? t.pass : null,
        issues: Array.isArray(t.issues) ? t.issues.filter(isObj) : [],
      };
    }
  }
  return out;
}

/** Замечание — у своего вопроса; без номера или с номером не из задания — у самого задания. */
export function splitIssues(
  v: TaskVerdict | undefined,
  ords: number[],
): { byOrd: Record<number, PlacedIssue[]>; general: PlacedIssue[] } {
  const known = new Set(ords);
  const byOrd: Record<number, PlacedIssue[]> = {};
  const general: PlacedIssue[] = [];
  for (const stage of ['quality', 'format'] as const) {
    for (const issue of v?.[stage]?.issues ?? []) {
      const placed = { ...issue, stage };
      const ord = Number(issue.question_ord);
      if (known.has(ord)) (byOrd[ord] ||= []).push(placed);
      else general.push(placed);
    }
  }
  return { byOrd, general };
}

// ── Решение и перезапуск ──

export type ApproveBlock = 'busy' | 'not_pending' | 'not_done' | 'comment_required' | 'exclude_all';
export type RejectBlock = 'busy' | 'already_rejected' | 'comment_required';
export type RerunBlock = 'busy' | 'approved_generate';

/** Почему нельзя одобрить (null — можно). Те же условия проверяет сервер. */
export function approveBlock(
  item: ItemLike,
  opts: { comment: string; excluded: number; total: number },
): ApproveBlock | null {
  if (isBusy(item)) return 'busy';
  if (item?.review_status !== 'pending') return 'not_pending';
  if (item.pipeline_state !== 'done') return 'not_done';
  if (item.checks_passed === false && !opts.comment.trim()) return 'comment_required';
  if (opts.total > 0 && opts.excluded >= opts.total) return 'exclude_all';
  return null;
}

/** Отклонить ожидающий или снять одобрение — всегда с комментарием. */
export function rejectBlock(item: ItemLike, comment: string): RejectBlock | null {
  if (isBusy(item)) return 'busy';
  if (item?.review_status === 'rejected') return 'already_rejected';
  if (!comment.trim()) return 'comment_required';
  return null;
}

// ── Публикация (29.09.2026, решение владельца: «одобрил» ≠ «опубликовал») ──

export type PublishBlock = 'busy' | 'not_approved';

/** Открыт ли одобренный элемент ученикам. */
export function isPublished(item: ItemLike): boolean {
  return !!item?.published_at;
}

/** Почему нельзя опубликовать или снять (null — можно). Те же условия проверяет сервер. */
export function publishBlock(item: ItemLike): PublishBlock | null {
  if (isBusy(item)) return 'busy';
  if (item?.review_status !== 'approved') return 'not_approved';
  return null;
}

/** Одобренный элемент перегенерировать нельзя — сначала снять одобрение; перепроверять можно. */
export function rerunBlock(item: ItemLike, stage: string): RerunBlock | null {
  if (isBusy(item)) return 'busy';
  if (stage === 'generate' && item?.review_status === 'approved') return 'approved_generate';
  return null;
}

// ── Промпты ──

/** Тот же разбор, что у службы: `placeholderRe` в `service/ai_pipeline_prompts.go`. */
export function placeholdersIn(...texts: string[]): Set<string> {
  const out = new Set<string>();
  for (const s of texts) for (const m of s.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)) out.add(m[1]);
  return out;
}

export function lintPrompt(
  def: { required_placeholders?: string[] | null; allowed_placeholders?: string[] | null },
  system: string,
  userTemplate: string,
): { missing: string[]; unknown: string[] } {
  const have = placeholdersIn(system, userTemplate);
  const allowed = new Set(def.allowed_placeholders ?? []);
  return {
    missing: (def.required_placeholders ?? []).filter((n) => !have.has(n)),
    unknown: [...have].filter((n) => !allowed.has(n)),
  };
}

// ── Заказ и очередь ──

export type GenerateForm = {
  skill: string;
  types: string[];
  kind: string;
  subtype: string;
  level: string;
  listeningPart: ListeningPart;
};
export type GenerateBody =
  | { skill: string; types: string[]; level: string }
  | { skill: string; listening_part: ListeningPart; types: string[]; level: string }
  | { skill: string; kind: string; subtype: string; level: string }
  | { skill: string; level: string };

export function generateBody(f: GenerateForm): GenerateBody {
  if (f.skill === 'listening') return { skill: f.skill, listening_part: f.listeningPart, types: f.types, level: f.level };
  if (f.skill === 'reading') return { skill: f.skill, types: f.types, level: f.level };
  if (f.skill === 'writing') return { skill: f.skill, kind: f.kind, subtype: f.subtype, level: f.level };
  return { skill: f.skill, level: f.level };
}

export type QueueFilters = { review_status: string; skill: string; checks: string; state: string; level: string };
export const DEFAULT_FILTERS: QueueFilters = { review_status: 'pending', skill: '', checks: '', state: '', level: '' };

export function queueParams(f: QueueFilters, offset: number): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(f)) if (v) out[k] = v;
  return { ...out, limit: PAGE_SIZE, offset };
}

// ── Вывод ──
// Всё, что пришло от модели, показывается ТЕКСТОМ и не идёт в подстановки i18n:
// фигурные скобки в ответе модели не должны разбираться как подстановка.

export function isRecord(v: unknown): v is Record<string, unknown> {
  return isObj(v);
}

/** Строка из любого значения вердикта: пустое — пустая строка, объект — JSON. */
export function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}

/** JSON с отступами; null и undefined — пустая строка, а не слово «null». */
export function prettyJson(v: unknown): string {
  if (v == null) return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

/** Дата и время; кривое или пустое значение — пустая строка, а не «Invalid Date». */
export function formatDateTime(v: string | null | undefined, lang: string): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const locale = lang.startsWith('kk') ? 'kk-KZ' : lang.startsWith('en') ? 'en-GB' : 'ru-RU';
  return d.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Длительность запуска в секундах; нет конца или время кривое — null. */
export function durationSec(start?: string, finish?: string): number | null {
  if (!start || !finish) return null;
  const s = Math.round((Date.parse(finish) - Date.parse(start)) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

/** Адрес записи: только свой путь или http(s) — не `javascript:` из данных. */
export function safeMediaUrl(u?: string): string {
  if (!u) return '';
  if (u.startsWith('/') && !u.startsWith('//')) return u;
  try {
    const url = new URL(u);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
}
